<script lang="ts" setup>
import ExternalLink from "~/assets/icons/external-link.svg";
import {resolveLink} from "#magnolia-layer/utils/link-utils";
import {NuxtLink} from "#components";
import parsePhoneNumber, {type CountryCode} from "libphonenumber-js/min";
import type {HydratedSpecialist} from "~/types/geocoded";

type Props = {
  specialist: HydratedSpecialist;
  selected?: boolean;
};

const {specialist} = defineProps<Props>();
const emit = defineEmits<{selected: [specialist: HydratedSpecialist]}>();
const {t, locale} = useI18n();
const onSelectSpecialist = (specialist: HydratedSpecialist) => {
  emit("selected", specialist);
};

const resolvedLink = computed(() => {
  const resolvedLink = resolveLink(specialist);
  return resolvedLink.link;
});
const phone = computed(() => {
  const country = (specialist.address?.country ?? "NL") as CountryCode;
  return specialist?.phone
    ? parsePhoneNumber(specialist.phone, country)
    : undefined;
});

const resolvedImage = didResolve(specialist.logo) ? specialist.logo : null;

const getCountryLabel = (countryCode: string, locale: string = "en") => {
  return new Intl.DisplayNames(locale, {type: "region"}).of(countryCode);
};

const countryTranslated = computed(() => {
  const c = specialist.address?.country;
  return c ? getCountryLabel(c, locale.value) : undefined;
});
</script>
<template>
  <div
    class="flex flex-col sm:flex-row gap-4 group cursor-pointer px-6 sm:px-0 py-3 hover:bg-neutral-800 transition-all"
    :class="{'bg-neutral-900': selected}"
    @click="onSelectSpecialist(specialist)">
    <div class="w-full sm:w-40 flex justify-center sm:items-start">
    <MagnoliaImg
      v-if="resolvedImage"
      class="grayscale w-32 max-w-32 sm:m-6 sm:mr-2"
      :asset="resolvedImage"></MagnoliaImg>
    </div>
    <div class="border-l border-white"></div>
    <div class="flex flex-col gap-2 w-full">
      <component
        :is="resolvedLink ? NuxtLink : 'div'"
        class="uppercase text-2xl text-inherit flex justify-between"
        :class="{'hover:text-sunrise-100': resolvedLink}"
        :href="resolvedLink"
        target="_blank">
        <div class="line-clamp-2">{{ specialist.company }}</div>
        <div class="min-w-4 min-h-5 my-1 mx-4" v-if="resolvedLink">
          <ExternalLink class="text-base" />
        </div>
      </component>
      <div class="text-lg">{{ specialist.description }}</div>
      <div class="text-sm uppercase flex flex-col">
        <span v-if="specialist.address"
          >{{ specialist.address.street }} {{ specialist.address.number }}</span
        >
        <span v-if="specialist.address"
          >{{ specialist.address.postalCode }}
          {{ specialist.address.city }}</span
        >
        <span>{{ countryTranslated }}</span>
        <NuxtLink
          v-if="resolvedLink"
          :href="resolvedLink"
          target="_blank"
          class="text-inherit hover:text-sunrise-100 w-fit"
          >{{ resolvedLink }}</NuxtLink
        >
        <NuxtLink
          v-if="phone && phone.isValid()"
          :href="`${phone.format('RFC3966')}`"
          target="_blank"
          class="text-inherit hover:text-sunrise-100 w-fit"
          >{{ phone.format("INTERNATIONAL") }}</NuxtLink
        >
      </div>
    </div>
  </div>
</template>
